/**
 * airan-ntn — Core Type Definitions
 * O-RAN NTN Simulator: Satellite constellation, xApps, Space RIC, Federated Learning
 */

// ──────────────────────────────────────────────────────────────────────────────
// Basic Geometry & Math
// ──────────────────────────────────────────────────────────────────────────────
export interface Vec3 { x: number; y: number; z: number; }
export interface Vec2 { x: number; y: number; }

export function vec3Add(a: Vec3, b: Vec3): Vec3 { return { x: a.x + b.x, y: a.y + b.y, z: a.z + b.z }; }
export function vec3Sub(a: Vec3, b: Vec3): Vec3 { return { x: a.x - b.x, y: a.y - b.y, z: a.z - b.z }; }
export function vec3Scale(v: Vec3, s: number): Vec3 { return { x: v.x * s, y: v.y * s, z: v.z * s }; }
export function vec3Len(v: Vec3): number { return Math.sqrt(v.x*v.x + v.y*v.y + v.z*v.z); }
export function vec3Norm(v: Vec3): Vec3 { const l = vec3Len(v); return l ? vec3Scale(v, 1/l) : {x:0,y:0,z:0}; }
export function vec3Dot(a: Vec3, b: Vec3): number { return a.x*b.x + a.y*b.y + a.z*b.z; }
export function vec3Cross(a: Vec3, b: Vec3): Vec3 { return { x: a.y*b.z - a.z*b.y, y: a.z*b.x - a.x*b.z, z: a.x*b.y - a.y*b.x }; }

// ──────────────────────────────────────────────────────────────────────────────
// Enums & Constants (matching oran-ntn C++ definitions)
// ──────────────────────────────────────────────────────────────────────────────

export type OrbitType = 'LEO' | 'MEO' | 'GEO';
export type ChannelModel = 'AWGN' | 'Rayleigh' | 'Rician' | 'NTN-LOS' | 'NTN-NLOS';
export type FLAlgorithm = 'FedAVG' | 'FedSGD' | 'FedProx';
export type SchedulerType =
  | 'Random' | 'RoundRobin' | 'MaxMin' | 'MaxMax' | 'DCC' | 'Grover'
  | 'Spatial' | 'Beamforming' | 'VirtualMIMO'
  | 'NtnElevation' | 'NtnDoppler' | 'NtnFeederAware';

export type XAppType =
  | 'ho-predict' | 'beam-hop' | 'slice-manager' | 'doppler-comp'
  | 'tn-ntn-steering' | 'energy-harvest' | 'interference-mgmt'
  | 'multi-conn' | 'predictive-alloc';

export interface Scheduler {
  readonly name: SchedulerType;
  select(ues: UserEquipment[], k: number, round?: number, satellites?: Satellite[], config?: SimConfig): UserEquipment[];
  reset(): void;
}

export const XAPP_TYPES: XAppType[] = [
  'ho-predict', 'beam-hop', 'slice-manager', 'doppler-comp',
  'tn-ntn-steering', 'energy-harvest', 'interference-mgmt',
  'multi-conn', 'predictive-alloc'
];

export type E2RcActionType =
  | 'HANDOVER_TRIGGER' | 'HANDOVER_CANCEL'
  | 'BEAM_HOP_SCHEDULE' | 'BEAM_SWITCH'
  | 'SLICE_PRB_ALLOCATION'
  | 'DOPPLER_COMP_UPDATE' | 'TIMING_ADVANCE_UPDATE'
  | 'TX_POWER_CONTROL' | 'CCA_THRESHOLD_ADJUST'
  | 'BEAM_SHUTDOWN' | 'COMPUTE_THROTTLE'
  | 'INTERFERENCE_NULLING'
  | 'DC_SETUP' | 'DC_TEARDOWN' | 'BEARER_SPLIT'
  | 'PRB_RESERVATION'
  | 'ENERGY_PROFILE_UPDATE';

export type A1PolicyType =
  | 'UE_THROUGHPUT' | 'UE_LATENCY' | 'UE_RELIABILITY'
  | 'CELL_LOAD' | 'SLICE_THROUGHPUT' | 'SLICE_LATENCY'
  | 'ENERGY_EFFICIENCY' | 'HANDOVER_RATE' | 'BEAM_UTILIZATION'
  | 'FL_CONVERGENCE' | 'FL_PRIVACY';

export type SatelliteState = 'ACTIVE' | 'ECLIPSE' | 'FEEDER_OUTAGE' | 'AUTONOMOUS' | 'SYNCING';

// ──────────────────────────────────────────────────────────────────────────────
// Orbital Mechanics
// ──────────────────────────────────────────────────────────────────────────────

export interface OrbitalElements {
  semiMajorAxis: number;    // meters
  eccentricity: number;     // 0 for circular
  inclination: number;      // radians
  raan: number;             // Right Ascension of Ascending Node (radians)
  argPerigee: number;       // Argument of Perigee (radians)
  trueAnomaly: number;      // True Anomaly (radians)
  epoch: number;            // Reference time (seconds)
}

export interface Satellite {
  id: number;
  name: string;
  orbitType: OrbitType;
  orbitalElements: OrbitalElements;
  position: Vec3;           // ECEF coordinates (meters)
  velocity: Vec3;           // ECEF velocity (m/s)
  // RF/Communication
  txPower: number;          // dBm
  antennas: number;         // Number of antenna elements
  carrierFreqGHz: number;   // Carrier frequency
  bandwidthMHz: number;     // Bandwidth
  // State
  state: SatelliteState;
  batterySoc: number;       // 0-1 State of Charge
  solarPower: number;       // Watts generated
  computeBudget: number;    // MIPS available for dApps
  // ISL
  islNeighbors: number[];   // Neighbor satellite IDs
  islLinks: ISLLink[];
  // Feeder link
  feederLink: FeederLink | null;
  // FL
  localModel: ModelWeights | null;
  localGradients: ModelWeights | null;
  flRound: number;
  // Visualization
  color: string;
  trail: Vec3[];
}

export interface ISLLink {
  neighborId: number;
  distance: number;         // meters
  latency: number;          // seconds
  capacity: number;         // bps
  type: 'laser' | 'rf';
  active: boolean;
}

export interface FeederLink {
  groundStationId: number;
  elevation: number;        // degrees
  snr: number;              // dB
  capacity: number;         // bps
  latency: number;          // seconds
  active: boolean;
  lastContact: number;      // Simulation time of last contact
}

export interface GroundStation {
  id: number;
  name: string;
  position: Vec3;           // ECEF
  lat: number;              // degrees
  lon: number;              // degrees
  alt: number;              // meters
  elevationMask: number;    // Minimum elevation angle (degrees)
  antennas: number;
  txPower: number;          // dBm
  // Connected satellites
  connectedSats: number[];
}

// ──────────────────────────────────────────────────────────────────────────────
// User Equipment (UE)
// ──────────────────────────────────────────────────────────────────────────────

export interface UserEquipment {
  id: number;
  position: Vec3;           // ECEF
  velocity: Vec3;           // m/s
  // Association
  servingSatId: number | null;     // Current serving satellite
  candidateSats: number[];         // Visible satellites (for handover)
  // Channel
  snr: number;              // dB (uplink)
  sinr: number;             // dB
  pathLoss: number;         // dB
  dopplerShift: number;     // Hz
  channelGain: number;      // Linear |h|^2
  // Mobility (Lévy walk on Earth surface)
  walkTarget: Vec3;
  walkRemaining: number;    // meters
  pauseLeft: number;        // rounds
  trail: Vec3[];
  // FL
  dataSize: number;         // Number of local samples
  localWeights: ModelWeights;
  selected: boolean;        // Selected for this FL round
  lastRound: number;        // Last round participated
  // Serving satellite
  bsId: number;             // Current serving satellite ID
  angle: number;            // Azimuth to serving satellite
  // Energy
  batterySoc: number;       // 0-1
  txPower: number;          // dBm
  // Visualization
  color: string;
}

// ──────────────────────────────────────────────────────────────────────────────
// KPM Reports & RC Actions (matching oran-ntn types)
// ──────────────────────────────────────────────────────────────────────────────

export interface E2KpmReport {
  // Identification
  gnbId: number;            // Satellite ID (serving as gNB)
  timestamp: number;        // Simulation time
  // RF Measurements (per UE served)
  ueReports: UeKpmReport[];
  // Cell-level
  cellLoad: number;         // 0-1 PRB utilization
  numActiveUes: number;
  totalPrbs: number;
  usedPrbs: number;
  // Slice metrics
  sliceMetrics: SliceKpmReport[];
  // Satellite-specific
  elevation: number;        // degrees to UE
  dopplerShift: number;     // Hz
  timeToEclipse: number;    // seconds
  batterySoc: number;       // 0-1
  solarPower: number;       // Watts
  // Beamforming
  beamIndex: number;
  beamGain: number;         // dB
  // Dual connectivity
  dcActive: boolean;
  dcBearers: number;
}

export interface UeKpmReport {
  ueId: number;
  rsrp: number;             // dBm
  rsrq: number;             // dB
  sinr: number;             // dB
  cqi: number;              // 1-15
  throughput: number;       // Mbps
  latency: number;          // ms
  harqAckRate: number;      // 0-1
  harqNackRate: number;     // 0-1
  mcs: number;              // 0-28
  prbUtilization: number;   // 0-1
  bufferStatus: number;     // Bytes
}

export interface SliceKpmReport {
  sliceId: number;          // eMBB=1, URLLC=2, mMTC=3
  throughput: number;       // Mbps
  latency: number;          // ms
  reliability: number;      // 0-1
  prbAllocated: number;
  prbUsed: number;
  ueCount: number;
}

export interface E2RcAction {
  type: E2RcActionType;
  targetId: number;         // UE ID or Cell ID
  payload: Record<string, unknown>;
  priority: number;         // 1-100
  timestamp: number;
}

export interface A1Policy {
  type: A1PolicyType;
  scope: 'UE' | 'CELL' | 'SLICE' | 'GLOBAL';
  targetId: number;
  threshold: number;
  operator: '>=' | '<=' | '==' | '!=' | '>' | '<';
  enabled: boolean;
}

// ──────────────────────────────────────────────────────────────────────────────
// xApp Framework
// ──────────────────────────────────────────────────────────────────────────────

export interface XAppConfig {
  type: XAppType;
  enabled: boolean;
  priority: number;         // Decision priority (1=highest)
  decisionInterval: number; // ms
  // Subscription
  requiredEventTriggers: string[];
  requiredReportStyle: string;
}

export interface XAppDecision {
  actions: E2RcAction[];
  confidence: number;       // 0-1
  latencyMs: number;        // Decision cycle latency
}

export interface XAppBase {
  readonly config: XAppConfig;
  readonly name: string;
  processKpmReport(report: E2KpmReport): void;
  decisionCycle(): XAppDecision;
  getRequiredSubscription(): { eventTriggers: string[]; reportStyle: string };
  reset(): void;
}

// ──────────────────────────────────────────────────────────────────────────────
// Federated Learning
// ──────────────────────────────────────────────────────────────────────────────

export interface ModelWeights {
  w: Float32Array;          // Flattened [numClasses * numFeatures]
  b: Float32Array;          // [numClasses]
  numFeatures: number;
  numClasses: number;
  version: number;          // Model version for sync
}

export interface TrainSample {
  x: Float32Array;          // Features
  y: number;                // Label
}

export interface FLConfig {
  algorithm: FLAlgorithm;
  scheduler: SchedulerType;
  localEpochs: number;
  learningRate: number;
  mu: number;               // FedProx proximal weight
  selectedPerRound: number;
  totalRounds: number;
  numFeatures: number;
  numClasses: number;
  samplesPerUE: number;
  nonIIDAlpha: number;      // Dirichlet α
  // NTN-specific
  ntnAwareScheduling: boolean;
  minElevationDeg: number;
  maxDopplerHz: number;
  requireFeederLink: boolean;
}

export interface RoundMetrics {
  round: number;
  selectedIds: number[];
  globalLoss: number;
  globalAccuracy: number;
  avgSNR: number;
  minSNR: number;
  participationRate: number;
  commCostBits: number;
  // NTN-specific
  avgElevation: number;
  avgDoppler: number;
  feederLinkUptime: number;    // Fraction of UEs with active feeder
  handovers: number;
  satelliteEnergy: number;     // Total energy consumed
}

export interface FLStrategy {
  readonly name: FLAlgorithm;
  localTrain(ue: UserEquipment, globalWeights: ModelWeights, data: TrainSample[], config: SimConfig): void;
  aggregate(globalWeights: ModelWeights, participants: UserEquipment[], allData: Map<number, TrainSample[]>, config: SimConfig): ModelWeights;
}

// ──────────────────────────────────────────────────────────────────────────────
// Space RIC & dApps
// ──────────────────────────────────────────────────────────────────────────────

export interface DAppConfig {
  name: string;
  enabled: boolean;
  computeBudget: number;    // MIPS
  modelType: 'dqn' | 'decision_tree' | 'linear';
  actionSpace: number;      // Number of discrete actions
  observationDim: number;
}

export interface DAppDecision {
  action: number;
  confidence: number;
  latencyMs: number;
}

export interface SpaceRicConfig {
  satelliteId: number;
  enabledDApps: string[];
  autonomousMode: boolean;
  feederOutageThreshold: number;  // Seconds without contact before autonomous
  modelSyncInterval: number;      // Rounds between model sync with ground
  inferenceEngine: 'dqn' | 'decision_tree' | 'linear';
  computeBudget: number;          // Total MIPS for all dApps
  islCoordination: boolean;
  gradientCompression: number;    // Top-k ratio (0-1)
  eclipseAwareFl: boolean;
}

export interface SpaceRicState {
  satelliteId: number;
  autonomous: boolean;
  feederOutageDuration: number;
  dAppDecisions: Map<string, DAppDecision>;
  localGradients: ModelWeights | null;
  globalModelVersion: number;
  islNeighbors: Map<number, SpaceRicState>;  // Neighbor states (simplified)
  pendingDecisions: E2RcAction[];  // Buffered for reconciliation
}

// ──────────────────────────────────────────────────────────────────────────────
// Simulation Configuration & State
// ──────────────────────────────────────────────────────────────────────────────

export interface SimConfig {
  // Constellation
  numOrbitals: number;      // Satellites per plane
  numPlanes: number;        // Orbital planes
  inclination: number;      // Degrees
  altitude: number;         // km (LEO: 500-2000, GEO: 35786)
  orbitType: OrbitType;
  // ISL
  enableISL: boolean;
  maxISLRange: number;      // km
  islType: 'laser' | 'rf';
  // Ground stations
  numGroundStations: number;
  gsPositions: Vec2[];      // [lat, lon] pairs
  // Channel
  carrierFreqGHz: number;
  bandwidthMHz: number;
  channelModel: ChannelModel;
  pathLossExponent: number; // 0 = use 3GPP slope
  shadowingStd: number;     // dB
  ricianK: number;
  snrThreshold: number;     // dB
  dopplerEnabled: boolean;
  // Mobility
  numUEs: number;
  ueSpeed: number;          // m/s
  walkEnabled: boolean;
  walkSpeed: number;        // m/s
  roundDuration: number;    // seconds per FL round
  levyAlpha: number;
  levyBeta: number;
  levyMinStep: number;      // meters
  // FL
  flAlgorithm: FLAlgorithm;
  scheduler: SchedulerType;
  localEpochs: number;
  learningRate: number;
  mu: number;
  selectedPerRound: number;
  totalRounds: number;
  numFeatures: number;
  numClasses: number;
  samplesPerUE: number;
  nonIIDAlpha: number;
  bsCooperation: boolean;
  // NTN-specific FL
  ntnAwareScheduling: boolean;
  minElevationDeg: number;
  maxDopplerHz: number;
  requireFeederLink: boolean;
  // Space RIC
  spaceRicEnabled: boolean;
  autonomousMode: boolean;
  feederOutageThreshold: number;
  modelSyncInterval: number;
  islCoordination: boolean;
  gradientCompression: number;
  eclipseAwareFl: boolean;
  // xApps
  enabledXApps: XAppType[];
  // Simulation
  roundDurationMs: number;  // Animation speed (ms per round)
  seed: number;
}

export interface SimState {
  config: SimConfig;
  satellites: Satellite[];
  groundStations: GroundStation[];
  ues: UserEquipment[];
  globalWeights: ModelWeights;
  round: number;
  history: RoundMetrics[];
  // Space RIC
  spaceRics: Map<number, SpaceRicState>;
  // xApps
  xApps: Map<XAppType, XAppBase>;
  // KPM/RC
  kpmDatabase: Map<number, E2KpmReport[]>;  // Per satellite
  pendingActions: E2RcAction[];
  // Feeder link tracking
  feederLinkHistory: Map<number, { active: boolean; timestamp: number }[]>;
}

export const DEFAULT_CONFIG: SimConfig = {
  // Constellation (Walker Delta: 72 sats, 6 planes, 550km, 53° inc - Starlink-like)
  numOrbitals: 12,
  numPlanes: 6,
  inclination: 53,
  altitude: 550,
  orbitType: 'LEO',
  // ISL
  enableISL: true,
  maxISLRange: 5000,
  islType: 'laser',
  // Ground stations
  numGroundStations: 4,
  gsPositions: [
    { x: 37.44, y: -122.14 },   // San Francisco
    { x: 40.71, y: -74.01 },    // New York
    { x: 51.51, y: -0.13 },     // London
    { x: 35.68, y: 139.69 },    // Tokyo
  ],
  // Channel (3GPP TS 38.901 NTN)
  carrierFreqGHz: 2.6,        // n256 / S-band
  bandwidthMHz: 20,
  channelModel: 'NTN-LOS',
  pathLossExponent: 0,
  shadowingStd: 4,
  ricianK: 10,
  snrThreshold: 0,
  dopplerEnabled: true,
  // Mobility
  numUEs: 20,
  ueSpeed: 1.4,
  walkEnabled: true,
  walkSpeed: 1.4,
  roundDuration: 5.0,
  levyAlpha: 1.8,
  levyBeta: 1.5,
  levyMinStep: 10,
  // FL
  flAlgorithm: 'FedAVG',
  scheduler: 'NtnElevation',
  localEpochs: 5,
  learningRate: 0.05,
  mu: 0.01,
  selectedPerRound: 8,
  totalRounds: 50,
  numFeatures: 4,
  numClasses: 3,
  samplesPerUE: 80,
  nonIIDAlpha: 0.5,
  bsCooperation: false,
  // NTN-specific FL
  ntnAwareScheduling: true,
  minElevationDeg: 10,
  maxDopplerHz: 5000,
  requireFeederLink: true,
  // Space RIC
  spaceRicEnabled: true,
  autonomousMode: true,
  feederOutageThreshold: 30,
  modelSyncInterval: 10,
  islCoordination: true,
  gradientCompression: 0.1,
  eclipseAwareFl: true,
  // xApps
  enabledXApps: [
    'ho-predict', 'beam-hop', 'slice-manager', 'doppler-comp',
    'tn-ntn-steering', 'energy-harvest', 'interference-mgmt',
    'multi-conn', 'predictive-alloc'
  ],
  // Simulation
  roundDurationMs: 300,
  seed: 42
};